import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy,
  Medal,
  Crown,
  Search,
  Award,
  Target,
  Zap,
  Star,
  ChevronRight,
  TrendingUp,
  Users,
  GraduationCap,
  BookOpen,
  Route,
  FileCheck
} from 'lucide-react';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import Pagination from '@/components/ui/Pagination';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { userAchievementsService } from '@/services/user-achievements.service';
import type { UserAchievementItem, UserAchievementMetrics, SortByField } from '@/types/user-achievements.types';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { USER_ACHIEVEMENTS_PERMISSION } from '@/constants/constant-permissions';

// Helper function to get initials from name
const getInitials = (name: string): string => {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export default function UserAchievements() {
  usePermissionRouteAccess([
    USER_ACHIEVEMENTS_PERMISSION.ADMIN_USER_ACHIEVEMENTS_READ,
  ]);

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortByField>('xp');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [isMetricsLoading, setIsMetricsLoading] = useState(true);
  const [users, setUsers] = useState<UserAchievementItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [metrics, setMetrics] = useState<UserAchievementMetrics>({
    total_users: 0,
    total_xp: 0,
    total_badges: 0,
    total_certificates: 0
  });

  const debouncedSearchTerm = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);

  // Fetch metrics on initial load
  const fetchMetrics = async () => {
    setIsMetricsLoading(true);
    try {
      const response = await userAchievementsService.getMetrics();
      setMetrics(response);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setIsMetricsLoading(false);
    }
  };

  const fetchUserAchievements = async () => {
    setIsLoading(true);
    try {
      const response = await userAchievementsService.listUserAchievements({
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortBy,
        sort_order: 'desc',
        search: debouncedSearchTerm || undefined
      });

      setUsers(response.data);
      setTotalItems(response.total);
    } catch (error) {
      console.error('Error fetching user achievements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch metrics once on mount
  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    fetchUserAchievements();
  }, [currentPage, itemsPerPage, sortBy, debouncedSearchTerm]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, sortBy]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const getRankStyle = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 to-gray-500 text-white';
    if (rank === 3) return 'bg-gradient-to-br from-amber-400 to-amber-600 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-4 h-4" />;
    if (rank === 2) return <Medal className="w-4 h-4" />;
    if (rank === 3) return <Trophy className="w-4 h-4" />;
    return <span className="text-sm font-bold">#{rank}</span>;
  };

  const ProfileImage = ({ src, alt, size = 'md' }: { src?: string; alt: string; size?: 'sm' | 'md' | 'lg' }) => {
    const sizeClasses = size === 'sm' ? 'w-10 h-10' : size === 'lg' ? 'w-16 h-16' : 'w-12 h-12';
    const textSizes = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-xl' : 'text-sm';
    const hasImage = src && src.trim() !== '';
    const initials = getInitials(alt);

    if (hasImage) {
      return <img src={src} alt={alt} className={`${sizeClasses} rounded-full object-cover`} />;
    }

    return (
      <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-teal-400 to-emerald-600 flex items-center justify-center`}>
        <span className={`${textSizes} font-semibold text-white`}>{initials}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600 rounded-2xl p-6 md:p-8 text-white">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg">
                <Award className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Learner Achievements</h1>
                <p className="text-white/80 mt-1">Celebrate your team's learning milestones and certificates</p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-white/80" />
                <span className="text-sm text-white/80">Learners</span>
              </div>
              {isMetricsLoading ? (
                <SkeletonLoader type="custom" className="h-8 w-16 rounded bg-white/20 mb-1" />
              ) : (
                <div className="text-2xl md:text-3xl font-bold">{metrics.total_users}</div>
              )}
              <div className="flex items-center gap-1 mt-1 text-emerald-300 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>Active</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-white/80" />
                <span className="text-sm text-white/80">Total XP</span>
              </div>
              {isMetricsLoading ? (
                <SkeletonLoader type="custom" className="h-8 w-20 rounded bg-white/20 mb-1" />
              ) : (
                <div className="text-2xl md:text-3xl font-bold">{metrics.total_xp.toLocaleString()}</div>
              )}
              <div className="flex items-center gap-1 mt-1 text-emerald-300 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>Earned</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-white/80" />
                <span className="text-sm text-white/80">Badges</span>
              </div>
              {isMetricsLoading ? (
                <SkeletonLoader type="custom" className="h-8 w-12 rounded bg-white/20 mb-1" />
              ) : (
                <div className="text-2xl md:text-3xl font-bold">{metrics.total_badges}</div>
              )}
              <div className="flex items-center gap-1 mt-1 text-emerald-300 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>Earned</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="w-5 h-5 text-white/80" />
                <span className="text-sm text-white/80">Certificates</span>
              </div>
              {isMetricsLoading ? (
                <SkeletonLoader type="custom" className="h-8 w-12 rounded bg-white/20 mb-1" />
              ) : (
                <div className="text-2xl md:text-3xl font-bold">{metrics.total_certificates}</div>
              )}
              <div className="flex items-center gap-1 mt-1 text-emerald-300 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>Awarded</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name,email or employee ID..."
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
            />
          </div>

          {/* Sort By */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setSortBy('xp')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                sortBy === 'xp' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Zap className="w-4 h-4 inline mr-1" />
              XP
            </button>
            <button
              onClick={() => setSortBy('badges')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                sortBy === 'badges' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Award className="w-4 h-4 inline mr-1" />
              Badges
            </button>
            <button
              onClick={() => setSortBy('certificates')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                sortBy === 'certificates' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileCheck className="w-4 h-4 inline mr-1" />
              Certs
            </button>
          </div>
        </div>
      </div>

      {/* Loading State - Skeleton */}
      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="card">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Left: Rank + User Info Skeleton */}
                <div className="flex items-center gap-4 flex-1">
                  {/* Rank Badge Skeleton */}
                  <SkeletonLoader type="custom" className="w-12 h-12 rounded-xl" />

                  {/* Profile Image Skeleton */}
                  <SkeletonLoader type="custom" className="w-16 h-16 rounded-full" />

                  {/* User Details Skeleton */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <SkeletonLoader type="custom" className="h-5 w-40 rounded" />
                    <SkeletonLoader type="custom" className="h-4 w-56 rounded" />
                    <div className="flex items-center gap-3">
                      <SkeletonLoader type="custom" className="h-6 w-20 rounded-full" />
                      <SkeletonLoader type="custom" className="h-4 w-16 rounded" />
                      <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                    </div>
                  </div>
                </div>

                {/* Middle: Stats Skeleton */}
                <div className="flex items-center gap-4 md:gap-6 px-4 py-3 md:py-0 bg-gray-50 md:bg-transparent rounded-xl md:rounded-none">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="text-center min-w-[60px]">
                      <SkeletonLoader type="custom" className="w-4 h-4 mx-auto mb-1 rounded" />
                      <SkeletonLoader type="custom" className="h-5 w-10 mx-auto mb-1 rounded" />
                      <SkeletonLoader type="custom" className="h-3 w-8 mx-auto rounded" />
                    </div>
                  ))}
                </div>

                {/* Right: Arrow Skeleton */}
                <div className="hidden md:flex items-center">
                  <SkeletonLoader type="custom" className="w-10 h-10 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* User List */}
      {!isLoading && users.length > 0 && (
        <div className="space-y-4">
          {users.map((user) => (
            <div
              key={user.user_id}
              onClick={() => navigate(`/admin/gamification/user-achievements/${user.user_id}`)}
              className="group card hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-teal-200"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Left: Rank + User Info */}
                <div className="flex items-center gap-4 flex-1">
                  {/* Rank Badge */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getRankStyle(user.rank)}`}>
                    {getRankIcon(user.rank)}
                  </div>

                  {/* Profile Image */}
                  <div className="relative">
                    <ProfileImage src={user.profile_image} alt={user.username} size="lg" />
                    {user.rank <= 3 && (
                      <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white ${
                        user.rank === 1 ? 'bg-yellow-500' : user.rank === 2 ? 'bg-gray-400' : 'bg-amber-500'
                      }`}>
                        <Star className="w-3 h-3 text-white fill-white" />
                      </div>
                    )}
                  </div>

                  {/* User Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-lg truncate">{user.username}</h3>
                      {user.rank === 1 && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          Top Performer
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm truncate">{user.email}</p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {user.employee_id && (
                        <span className="text-xs px-2 py-1 bg-teal-50 text-teal-700 rounded-full">{user.employee_id}</span>
                      )}
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Level {user.total_level}
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        🔥 {user.longest_streak} day streak
                      </span>
                    </div>
                  </div>
                </div>

                {/* Middle: Stats */}
                <div className="flex items-center gap-4 md:gap-6 px-4 py-3 md:py-0 bg-gray-50 md:bg-transparent rounded-xl md:rounded-none overflow-x-auto">
                  <div className="text-center min-w-[60px]">
                    <div className="flex items-center justify-center gap-1 text-yellow-600 mb-1">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div className="font-bold text-gray-900">{user.total_xp.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">XP</div>
                  </div>

                  <div className="text-center min-w-[60px]">
                    <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
                      <Award className="w-4 h-4" />
                    </div>
                    <div className="font-bold text-gray-900">{user.badge_count}</div>
                    <div className="text-xs text-gray-500">Badges</div>
                  </div>

                  <div className="text-center min-w-[60px]">
                    <div className="flex items-center justify-center gap-1 text-teal-600 mb-1">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <div className="font-bold text-gray-900">{user.course_certificate_count}</div>
                    <div className="text-xs text-gray-500">Course</div>
                  </div>

                  <div className="text-center min-w-[60px]">
                    <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                      <GraduationCap className="w-4 h-4" />
                    </div>
                    <div className="font-bold text-gray-900">{user.certification_certificate_count}</div>
                    <div className="text-xs text-gray-500">Cert</div>
                  </div>

                  <div className="text-center min-w-[60px]">
                    <div className="flex items-center justify-center gap-1 text-emerald-600 mb-1">
                      <Route className="w-4 h-4" />
                    </div>
                    <div className="font-bold text-gray-900">{user.learning_path_certificate_count}</div>
                    <div className="text-xs text-gray-500">Path</div>
                  </div>
                </div>

                {/* Right: Arrow */}
                <div className="hidden md:flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 transition-colors" />
                  </div>
                </div>
              </div>

              {/* Mobile: View Details */}
              <div className="md:hidden mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">View all achievements</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && users.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && users.length === 0 && (
        <div className="card text-center py-16">
          <div className="w-20 h-20 mx-auto mb-4 bg-teal-50 rounded-full flex items-center justify-center">
            <Search className="w-10 h-10 text-teal-300" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No learners found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
}
